import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Store, Leaf, Users, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const VendorRegister = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    business_type: 'restaurant',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    description: '',
    specialties: [],
    is_farm_fresh: false,
    is_locally_sourced: false
  });

  const businessTypes = [
    { value: 'restaurant', label: 'Restaurant', icon: Store },
    { value: 'cafe', label: 'Cafe', icon: Store },
    { value: 'food_truck', label: 'Food Truck', icon: Store },
    { value: 'farm', label: 'Farm', icon: Leaf },
    { value: 'meal_prep_service', label: 'Meal Prep Service', icon: Users },
    { value: 'bakery', label: 'Bakery', icon: Store }
  ];

  const cuisineOptions = [
    'west_african', 'east_african', 'caribbean', 'western',
    'middle_eastern', 'south_asian', 'east_asian', 'southeast_asian',
    'latin_american', 'mediterranean'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const toggleSpecialty = (cuisine) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(cuisine)
        ? prev.specialties.filter(c => c !== cuisine)
        : [...prev.specialties, cuisine]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.specialties.length === 0) {
      toast.error('Please select at least one cuisine specialty');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/vendors/register`, {
        business_name: formData.business_name,
        owner_name: formData.owner_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        business_type: formData.business_type,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        description: formData.description,
        specialties: formData.specialties,
        is_farm_fresh: formData.is_farm_fresh,
        is_locally_sourced: formData.is_locally_sourced
      });

      if (response.data.success) {
        toast.success('Registration successful! Your application is pending approval.');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMsg = error.response?.data?.detail || 'Registration failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 p-4">
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Join HealthLoop Vendor Network
          </h1>
          <p className="text-lg text-gray-600">
            Partner with us to provide healthy meals to our community
          </p>
        </div>

        {/* Benefits Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-2 border-green-200 bg-white">
            <CardContent className="p-4 text-center">
              <Store className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <h3 className="font-semibold mb-1">Reach Health-Conscious Customers</h3>
              <p className="text-sm text-gray-600">Connect with users actively seeking healthy meals</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-blue-200 bg-white">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <h3 className="font-semibold mb-1">AI-Powered Matching</h3>
              <p className="text-sm text-gray-600">Your meals matched with personalized recommendations</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-purple-200 bg-white">
            <CardContent className="p-4 text-center">
              <Leaf className="h-8 w-8 mx-auto mb-2 text-purple-600" />
              <h3 className="font-semibold mb-1">Sustainability Focus</h3>
              <p className="text-sm text-gray-600">Earn badges for farm-fresh and local sourcing</p>
            </CardContent>
          </Card>
        </div>

        {/* Registration Form */}
        <Card>
          <CardHeader>
            <CardTitle>Vendor Registration</CardTitle>
            <CardDescription>
              Complete the form below to start your partnership with HealthLoop
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Business Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Fresh Farm Foods"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_name">Owner Name *</Label>
                    <Input
                      id="owner_name"
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., John Smith"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Business Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="vendor@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="business_type">Business Type *</Label>
                  <select
                    id="business_type"
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md"
                    required
                  >
                    {businessTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="description">Business Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us about your business, your mission, and what makes your meals special..."
                    rows={4}
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Location</h3>
                
                <div>
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="New York"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      placeholder="NY"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip_code">Zip Code *</Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      required
                      placeholder="10001"
                      maxLength={5}
                    />
                  </div>
                </div>
              </div>

              {/* Cuisine Specialties */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Cuisine Specialties *</h3>
                <p className="text-sm text-gray-600">Select all cuisines you offer</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {cuisineOptions.map(cuisine => (
                    <div
                      key={cuisine}
                      className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                        formData.specialties.includes(cuisine)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleSpecialty(cuisine)}
                    >
                      <div className="flex items-center">
                        <Checkbox
                          checked={formData.specialties.includes(cuisine)}
                          onCheckedChange={() => toggleSpecialty(cuisine)}
                        />
                        <span className="ml-2 text-sm font-medium capitalize">
                          {cuisine.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sustainability */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Sustainability & Sourcing</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 border-2 border-green-200 rounded-lg bg-green-50">
                    <Checkbox
                      id="is_farm_fresh"
                      checked={formData.is_farm_fresh}
                      onCheckedChange={(checked) => handleCheckboxChange('is_farm_fresh', checked)}
                    />
                    <div className="flex-1">
                      <Label htmlFor="is_farm_fresh" className="font-semibold">
                        Farm Fresh 🌾
                      </Label>
                      <p className="text-sm text-gray-600">
                        Meals made with farm-fresh ingredients (+30 sustainability points)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 p-3 border-2 border-blue-200 rounded-lg bg-blue-50">
                    <Checkbox
                      id="is_locally_sourced"
                      checked={formData.is_locally_sourced}
                      onCheckedChange={(checked) => handleCheckboxChange('is_locally_sourced', checked)}
                    />
                    <div className="flex-1">
                      <Label htmlFor="is_locally_sourced" className="font-semibold">
                        Locally Sourced 🏘️
                      </Label>
                      <p className="text-sm text-gray-600">
                        Ingredients sourced within 100 miles (+30 sustainability points)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Account Security</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        placeholder="••••••••"
                        className="pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-9 w-9 text-gray-400 hover:text-gray-600 rounded-md transition-colors"
                        aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                        aria-pressed={showPassword}
                        tabIndex={-1}
                        data-testid="vendor-register-password-toggle"
                      >
                        {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirm_password">Confirm Password *</Label>
                    <div className="relative">
                      <Input
                        id="confirm_password"
                        name="confirm_password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirm_password}
                        onChange={handleInputChange}
                        required
                        placeholder="••••••••"
                        className="pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center h-9 w-9 text-gray-400 hover:text-gray-600 rounded-md transition-colors"
                        aria-label={showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                        aria-pressed={showConfirmPassword}
                        tabIndex={-1}
                        data-testid="vendor-register-confirm-password-toggle"
                      >
                        {showConfirmPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Submitting...' : 'Register as Vendor Partner'}
                </Button>
              </div>

              <p className="text-sm text-gray-600 text-center">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/vendor-login')}
                  className="text-green-600 hover:underline font-semibold"
                >
                  Login here
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VendorRegister;
